/**
 * <NpsScoreScale> — 0–10 NPS score picker (molecule, L0.1).
 *
 * Horizontal segmented control for Net Promoter scores. Controlled `value` + `onSelect`;
 * composes `<Text>` + `Pressable`. No hex.
 */
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Text } from "../../atoms/typography/Text"

const SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const

export type NpsScoreScaleProps = {
    value: number | null
    onSelect: (score: number) => void
    disabled?: boolean
    testID?: string
}

export function NpsScoreScale(props: NpsScoreScaleProps) {
    const { value, onSelect, disabled = false, testID } = props

    return (
        <Row testID={testID} style={styles.row}>
            {SCORES.map((score, index) => {
                const selected = value === score
                return (
                    <Pressable
                        key={score}
                        disabled={disabled}
                        onPress={() => onSelect(score)}
                        style={styles.cell(selected, index === 0, disabled)}
                        accessibilityRole="button"
                        accessibilityState={{ selected, disabled }}
                        accessibilityLabel={`Note ${score}`}
                    >
                        <Text size="text-sm" weight={selected ? "bold" : "regular"} color="primary">
                            {score}
                        </Text>
                    </Pressable>
                )
            })}
        </Row>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: {
        width: "100%",
        alignSelf: "stretch" as const,
    },
    cell: (selected: boolean, isFirst: boolean, disabled: boolean) => ({
        flex: 1,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        paddingVertical: theme.spacing["4"],
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderRightWidth: 1,
        borderLeftWidth: isFirst || selected ? 1 : 0,
        borderColor: selected ? theme.colors.primary : theme.colors.border,
        backgroundColor: selected ? theme.colors["primary-soft"] : theme.colors.surface,
        opacity: disabled ? 0.5 : 1,
    }),
}))
